// src/utils/clientCoachingMemory.js
//
// Pass 79 — the ONE pure, DERIVED longitudinal client coaching memory model.
//
// IDENTITY (hard boundary): `clientId` = the COMMERCIAL customer/family/lead (may have NO Firebase Auth).
// `studentId` = an EXPLICIT participant/Auth identity. They are NEVER interchangeable. A commercial client may have
// MANY independent participants (a family: mum pays, child A skis, child B snowboards). Participant coaching memory
// is grouped ONLY by an explicit `lessonInstance.studentId`; it is NEVER grouped by name/email/household/booking/
// clientId. A delivered lesson with a clientId but NO stable participant id is an UNLINKED coaching event — it is
// never longitudinally merged with another unlinked lesson.
//
// This module is PURE (no IO, no Firebase, no AI, no Date.now for facts). It DERIVES from already-authorized
// canonical records; it creates no datastore and copies no history. Human evidence (video.analysis, lesson outcomes,
// manual notes) is authoritative; published AI (video.aiReview) is NEVER read here and can never create a memory fact.

import { normalizeSportName } from './drillTemplates';
import { getAnalysisSignals, normalizeReviewAnalysis } from './reviewAnalysis';
import { buildProgressionEvidence } from './progressionEvidence';
import { isDeliveryBearing } from './lessonHandoff';

function s(value) {
  return String(value || '').trim();
}

// Pass 79 REPAIR §3 — which video-owner ids may contribute reviewed participant evidence to a client workspace.
// CANONICAL clientId workspace: ONLY the explicit coached participants (from clientId-linked delivery evidence) —
// the commercial account holder / purchaser is NEVER auto-included (purchaser ≠ coached participant). If an
// Auth-backed commercial account is ALSO a real participant it already appears in participantIds via lesson lineage.
// LEGACY /clients/:clientUserId compatibility (no canonical clientId): the Auth client IS the participant.
export function resolveParticipantVideoOwnerIds({ clientId = '', clientUserId = '', participantIds = [] } = {}) {
  const explicit = [...new Set((participantIds || []).map((id) => s(id)).filter(Boolean))];
  if (s(clientId)) return explicit; // canonical: participants only
  return [...new Set([s(clientUserId), ...explicit].filter(Boolean))]; // legacy Auth-only
}

// Pass 79B REPAIR §3/§4 — the ONE canonical delivered-participant projection. Delivered coaching truth is
// `isDeliveryBearing(lesson)` (status completed OR genuine stage/outcome work), NEVER `status === 'completed'` alone.
// Every participant-safety / client-signals count derives from THIS so they can never disagree.
export function summarizeDeliveredParticipants(workspace = {}) {
  const deliveredLessons = (workspace.lessons || []).filter((l) => isDeliveryBearing(l));
  const participantIds = [...new Set(deliveredLessons.map((l) => s(l?.studentId)).filter(Boolean))];
  const hasUnlinked = deliveredLessons.some((l) => !s(l?.studentId)); // a delivery-bearing lesson with no studentId
  return { deliveredLessons, participantIds, hasUnlinked, participantCount: participantIds.length };
}

// A WORKSPACE-LEVEL personal progression summary is only safe when there is EXACTLY ONE explicit participant AND no
// unlinked DELIVERED coaching event. "One known participant" is NEVER proof that unlinked delivered lessons belong to
// them. Returns the participant-SCOPED inputs, or null (fail closed → the caller uses an empty progression summary).
export function selectParticipantSafeProgressionInputs(workspace = {}) {
  const { deliveredLessons, participantIds, hasUnlinked } = summarizeDeliveredParticipants(workspace);
  if (participantIds.length !== 1 || hasUnlinked) return null; // fail closed on the canonical delivery boundary
  const participantId = participantIds[0];
  return {
    participantId,
    videos: (workspace.videos || []).filter((v) => s(v?.userId) === participantId || s(v?.studentId) === participantId),
    lessons: deliveredLessons.filter((l) => s(l?.studentId) === participantId),
  };
}

export function toEventTime(value) {
  if (!value) return 0;
  if (typeof value?.toDate === 'function') return value.toDate().getTime();
  if (value?.seconds) return value.seconds * 1000;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? 0 : t;
}
function toTime(value) { return toEventTime(value); }

// §10 deterministic event-time precedence — explicit delivery/completion → scheduled → date → created. NEVER updatedAt.
// The ONE shared lesson event-time helper (Pass 80 §8): retention timing must never derive "when a service happened"
// from `updatedAt`.
export function lessonEventTime(lesson = {}) {
  return toTime(lesson.completedAt || lesson.deliveredAt || lesson.scheduledAt || lesson.date || lesson.serviceDate || lesson.createdAt);
}
function videoEventTime(video = {}) {
  return toTime(video.reviewedAt || video.createdAt);
}

// The participant of a lesson is its EXPLICIT studentId — nothing else. Blank → unlinked.
function participantIdOf(lesson = {}) {
  return s(lesson.studentId);
}

const PROGRESS_ORDER = { needs_review: 0, low: 0, developing: 1, solid: 2, ready_to_progress: 3 };
function progressRank(confidence) {
  const key = s(confidence).toLowerCase();
  return Object.prototype.hasOwnProperty.call(PROGRESS_ORDER, key) ? PROGRESS_ORDER[key] : -1;
}

// ── recurring patterns: distinct canonical HUMAN evidence events only (dedupe by source id) ──────────────
function buildRecurringPatterns(sportReviewedVideos, sportLessons, sport) {
  const byKey = new Map(); // patternKey -> { key, label, sport, sources:Set, kind }
  const add = (kind, key, label, sourceId) => {
    if (!key || !sourceId) return;
    const pk = `${kind}:${key}`;
    const rec = byKey.get(pk) || { key, label: label || key, sport, kind, sources: new Set() };
    rec.sources.add(sourceId); // §12/§34 dedupe: the SAME source counts once
    byKey.set(pk, rec);
  };
  // human review issues (structured movement analysis) across DISTINCT reviewed videos
  sportReviewedVideos.forEach((video) => {
    const analysis = normalizeReviewAnalysis(video, video?.analysis);
    const signals = getAnalysisSignals(analysis);
    (signals?.issues || []).forEach((item) => add('review_issue', item.key, item.label, `review:${s(video.id)}`));
  });
  // same delivered-lesson target skill across DISTINCT lessons
  sportLessons.forEach((lesson) => {
    const skillId = s(lesson.targetSkillId);
    if (skillId) add('lesson_target', skillId, skillId, `lesson:${s(lesson.id || lesson.lessonId)}`);
  });
  return Array.from(byKey.values())
    .filter((r) => r.sources.size >= 2) // recurrence = at least TWO distinct canonical sources
    .map((r) => ({ key: r.key, label: r.label, sport: r.sport, kind: r.kind, occurrences: r.sources.size, sourceIds: Array.from(r.sources).sort() }))
    .sort((a, b) => b.occurrences - a.occurrences || a.label.localeCompare(b.label));
}

// ── demonstrated improvement: conservative, same canonical target skill needs_review → later solid/ready ──
function buildDemonstratedProgress(sportLessons) {
  const bySkill = new Map();
  sportLessons.forEach((lesson) => {
    const skillId = s(lesson.targetSkillId);
    if (!skillId) return;
    const entry = bySkill.get(skillId) || [];
    entry.push({ at: lessonEventTime(lesson), confidence: s(lesson?.lessonOutcome?.skillConfidence), lessonId: s(lesson.id || lesson.lessonId) });
    bySkill.set(skillId, entry);
  });
  const improved = [];
  bySkill.forEach((events, skillId) => {
    const ordered = events.slice().sort((a, b) => a.at - b.at);
    const earliest = ordered[0];
    const latest = ordered[ordered.length - 1];
    // require DISTINCT earlier vs later lessons with a real upward transition in the SAME skill (§13)
    if (ordered.length >= 2 && earliest.lessonId !== latest.lessonId && progressRank(earliest.confidence) >= 0 && progressRank(latest.confidence) > progressRank(earliest.confidence)) {
      improved.push({ targetSkillId: skillId, from: earliest.confidence, to: latest.confidence, fromLessonId: earliest.lessonId, toLessonId: latest.lessonId });
    }
  });
  return improved.sort((a, b) => a.targetSkillId.localeCompare(b.targetSkillId));
}

function lastDrillTopic(sportReviewedVideos) {
  const newest = sportReviewedVideos.slice().sort((a, b) => videoEventTime(b) - videoEventTime(a))[0];
  const drills = newest?.reviewDrills || [];
  return s(drills[0]?.topic);
}

// ── one participant's memory for ONE sport ──────────────────────────────────────────────────────────────
function buildSportMemory(sport, sportLessons, sportReviewedVideos, clientGoals) {
  const recentLessons = sportLessons.slice().sort((a, b) => lessonEventTime(b) - lessonEventTime(a));
  const recentReviews = sportReviewedVideos.slice().sort((a, b) => videoEventTime(b) - videoEventTime(a));
  const recurringPatterns = buildRecurringPatterns(sportReviewedVideos, sportLessons, sport);
  const demonstratedProgress = buildDemonstratedProgress(sportLessons);

  // current focus + demonstrated level from the ONE Pass-77 contract, scoped to THIS participant + sport.
  const evidence = buildProgressionEvidence({ videos: sportReviewedVideos, lessonInstances: sportLessons, sport });
  const currentFocus = evidence.winner ? evidence.winner.skillFocus : '';

  const lastLesson = recentLessons[0] || null;
  // §15 NEXT INSTRUCTOR BRIEF — only supported facts (undefined fields omitted at render).
  const nextInstructorBrief = {
    lastLessonAt: lastLesson ? lessonEventTime(lastLesson) : 0,
    lastInstructor: s(lastLesson?.instructorNameSnapshot),
    workingOn: currentFocus,
    demonstratedLevel: evidence.levelBasis === 'lesson_demonstrated_skill' ? s(evidence.currentLevel) : '',
    demonstratedConfidence: s(lastLesson?.lessonOutcome?.skillConfidence),
    recurring: recurringPatterns[0] ? { label: recurringPatterns[0].label, occurrences: recurringPatterns[0].occurrences } : null,
    goals: clientGoals, // §14 CLIENT-STATED goal — distinct authority from the evidence-derived focus above
    lastDrill: lastDrillTopic(sportReviewedVideos),
  };

  return {
    sport,
    recentLessons: recentLessons.map((l) => ({ lessonId: s(l.id || l.lessonId), at: lessonEventTime(l), instructorName: s(l.instructorNameSnapshot), targetSkillId: s(l.targetSkillId), outcomeStatus: s(l?.lessonOutcome?.status), skillConfidence: s(l?.lessonOutcome?.skillConfidence) })),
    recentReviews: recentReviews.map((v) => ({ videoId: s(v.id), at: videoEventTime(v), route: s(v.id) ? `/reviews/${s(v.id)}` : '' })),
    recurringPatterns,
    demonstratedProgress,
    currentFocus,
    hasProgressionEvidence: evidence.hasEvidence,
    nextInstructorBrief,
  };
}

function buildParticipantMemory(participant, { clientGoals }) {
  const { participantId, displayLabel, lessons, videos } = participant;
  const sports = new Set();
  lessons.forEach((l) => sports.add(normalizeSportName(l.sport || '') || 'unknown'));
  videos.forEach((v) => sports.add(normalizeSportName(v.sport || '') || 'unknown'));
  const sportMemories = Array.from(sports).sort().map((sport) => {
    const sportLessons = lessons.filter((l) => (normalizeSportName(l.sport || '') || 'unknown') === sport);
    const sportReviewed = videos.filter((v) => (normalizeSportName(v.sport || '') || 'unknown') === sport);
    return buildSportMemory(sport, sportLessons, sportReviewed, clientGoals);
  });
  return {
    participantId,
    displayLabel: displayLabel || 'Participant',
    sportMemories,
    lessonCount: lessons.length,
    reviewedVideoCount: videos.length,
  };
}

export function buildClientCoachingMemory({
  clientId = '',
  organizationId = '',
  bookings = [],
  bookingRequests = [],
  lessons = [],
  videos = [],
  notes = [],
  clientGoals = '',
  displayLabelsByParticipantId = {},
} = {}) {
  const cid = s(clientId);
  const deliveredLessons = (lessons || []).filter((l) => isDeliveryBearing(l)); // §11 delivery-bearing only
  const reviewedVideos = (videos || []).filter((v) => s(v.status) === 'reviewed');

  // group delivered lessons by EXPLICIT participant studentId; unlinked lessons are NOT merged (§6/§29)
  const byParticipant = new Map();
  const unlinkedCoachingEvents = [];
  deliveredLessons.forEach((lesson) => {
    const pid = participantIdOf(lesson);
    if (!pid) {
      unlinkedCoachingEvents.push({ lessonId: s(lesson.id || lesson.lessonId), at: lessonEventTime(lesson), sport: normalizeSportName(lesson.sport || ''), instructorName: s(lesson.instructorNameSnapshot), targetSkillId: s(lesson.targetSkillId), note: 'No linked participant identity' });
      return;
    }
    const rec = byParticipant.get(pid) || { participantId: pid, displayLabel: s(displayLabelsByParticipantId[pid]) || s(lesson.studentNameSnapshot), lessons: [], videos: [] };
    if (!rec.displayLabel) rec.displayLabel = s(displayLabelsByParticipantId[pid]) || s(lesson.studentNameSnapshot);
    rec.lessons.push(lesson);
    byParticipant.set(pid, rec);
  });

  // §8 a reviewed video enters a participant's memory ONLY via EXPLICIT participant lineage — the video is owned by
  // (userId) or assigned to (studentId) an id that is ALREADY a known participant (from a clientId-linked lesson).
  // The purchaser is never assumed to be the coached participant.
  reviewedVideos.forEach((video) => {
    const owner = s(video.userId);
    const stu = s(video.studentId);
    [owner, stu].forEach((pid) => {
      if (pid && byParticipant.has(pid) && !byParticipant.get(pid).videos.includes(video)) {
        byParticipant.get(pid).videos.push(video);
      }
    });
  });

  const participants = Array.from(byParticipant.values())
    .map((p) => buildParticipantMemory(p, { clientGoals }))
    .sort((a, b) => a.participantId.localeCompare(b.participantId));

  // commercial (client-level) chronology — bookings/requests/lessons/notes, event-time ordered
  const recentActivity = [
    ...(bookings || []).map((b) => ({ kind: 'booking', id: s(b.bookingId || b.id), at: toTime(b.startAt || b.scheduledAt || b.createdAt), label: s(b.serviceNameSnapshot || b.serviceType || 'Booking'), status: s(b.status) })),
    ...(bookingRequests || []).map((r) => ({ kind: 'booking_request', id: s(r.bookingRequestId || r.id), at: toTime(r.createdAt), label: s(r.serviceNameSnapshot || r.serviceType || 'Booking request'), status: s(r.status) })),
    ...deliveredLessons.map((l) => ({ kind: 'lesson', id: s(l.id || l.lessonId), at: lessonEventTime(l), label: 'Lesson delivered', sport: normalizeSportName(l.sport || ''), instructorName: s(l.instructorNameSnapshot), participantId: participantIdOf(l) })),
  ].filter((e) => e.at > 0).sort((a, b) => b.at - a.at);

  const deliveredCount = deliveredLessons.length;
  const lastServiceAt = recentActivity.find((e) => e.kind === 'lesson' || e.kind === 'booking')?.at || 0;
  const nextServiceAt = (bookings || [])
    .map((b) => toTime(b.startAt || b.scheduledAt))
    .filter((t) => t > 0)
    .sort((a, b) => a - b)
    .find(() => true) || 0;

  return {
    clientId: cid,
    organizationId: s(organizationId),
    commercialSummary: {
      bookingCount: (bookings || []).length,
      requestCount: (bookingRequests || []).length,
      deliveredLessonCount: deliveredCount,
      participantCount: participants.length,
      lastServiceAt,
      nextServiceAt,
    },
    participants,
    unlinkedCoachingEvents: unlinkedCoachingEvents.sort((a, b) => b.at - a.at),
    manualNotes: (notes || []).map((n) => ({ id: s(n.clientNoteId || n.id), at: toTime(n.createdAt), text: s(n.noteText), tags: Array.isArray(n.tags) ? n.tags : [], relatedLessonId: s(n.relatedLessonId) })).sort((a, b) => b.at - a.at),
    clientGoals: s(clientGoals),
    recentActivity,
  };
}
